import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1';

// Create axios instance with CORS support
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

export const login = async (email, password) => {
  try {
    const response = await apiClient.post('/login/', { email, password });
    
    // Check if account is inactive/unverified
    if (response.data.is_active === false || response.data.is_verified === false || response.data.account_status === 'inactive') {
      return {
        success: false,
        error: 'Account not verified',
        requiresVerification: true,
        email: email,
        data: response.data,
      };
    }
    
    // Extract access token with multiple fallbacks
    const extractedToken = 
      response.data.tokens?.access || 
      response.data.access_token || 
      response.data.token ||
      response.data.auth_token;
    
    // Extract refresh token with multiple fallbacks
    const refreshToken = 
      response.data.tokens?.refresh || 
      response.data.refresh_token ||
      response.data.token_refresh;
    
    
    return {
      success: true,
      data: response.data,
      token: extractedToken,
      refreshToken: refreshToken,
      user: { 
        email, 
        id: response.data.user_id,
        company_id: response.data.company_id,
        user_id: response.data.user_id,
        full_name: response.data.full_name
      },
    };
  } catch (error) {
    // Extract and format error message
    let errorMessage = extractErrorMessage(error);
    
    // Ensure it's a string
    if (typeof errorMessage !== 'string') {
      errorMessage = String(errorMessage);
    }
    
    if (errorMessage.toLowerCase().includes('not verified') || 
        errorMessage.toLowerCase().includes('inactive') ||
        errorMessage.toLowerCase().includes('verify') ||
        error.response?.data?.account_status === 'inactive') {
      return {
        success: false,
        error: 'Account not verified',
        requiresVerification: true,
        email: email,
      };
    }
    
    return {
      success: false,
      error: errorMessage,
    };
  }
};

export const register = async (email, password, name) => {
  try {
    const response = await apiClient.post('/register/', {
      email,
      full_name: name,
      password,
      second_password: password,
    });
    return { success: true, requiresOTP: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

export const verifyOTP = async (email, otpCode) => {
  try {
    const response = await apiClient.post('/verify-otp/', {
      email,
      verification_code: otpCode,
    });
    
    // Extract access token with multiple fallbacks
    const extractedToken = 
      response.data.tokens?.access || 
      response.data.access_token || 
      response.data.token ||
      response.data.auth_token;
    
    // Extract refresh token with multiple fallbacks
    const refreshToken = 
      response.data.tokens?.refresh || 
      response.data.refresh_token ||
      response.data.token_refresh;
    
    
    return {
      success: true,
      data: response.data,
      token: extractedToken,
      refreshToken: refreshToken,
      user: { 
        email, 
        id: response.data.user_id,
        company_id: response.data.company_id,
        user_id: response.data.user_id,
        full_name: response.data.full_name
      },
    };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

export const resendOTP = async (email) => {
  try {
    const response = await apiClient.post('/resend-otp/', { email });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

export const createCompanyProfile = async (profileData) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await apiClient.post('/company/profile/', profileData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    

    return { success: true, data: response.data };
  } catch (error) {
    
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

export const logout = async () => {
  try {
    // await axios.post(`${API_URL}/logout`);
    return Promise.resolve();
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const getCurrentUser = async () => {
  try {
    // const response = await axios.get(`${API_URL}/me`);
    // return response.data;
    return Promise.resolve({ user: { email: 'user@example.com', name: 'User' } });
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const resetPassword = async (email, password, secondPassword) => {
  try {
    const response = await apiClient.post('/reset-password/', {
      email,
      password,
      second_password: secondPassword,
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

export const checkCompanyProfile = async (userId, token) => {
  try {
    const response = await apiClient.get(`/company/profile/check/${userId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    
    return {
      success: true,
      company_id: response.data.company_id,
      has_company_profile: response.data.has_company_profile,
      business_type: response.data.business_type,
      full_name: response.data.full_name,
    };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};
